const { throttle, random } = require("lodash");

/**
 * Returns a unique event ID, that can be used to prevent duplicate events while working with front and back-end tagging
 */
function getUniqueEventId() {
    const sessionStorageKey = "unique_session_id";
    const eventId = random(100000, 999999, false);
    let sessionId = sessionStorage.getItem(sessionStorageKey);

    if (!sessionId) {
        sessionId = random(100000, 999999, false);

        sessionStorage.setItem(sessionStorageKey, sessionId);
    }

    return `${sessionId}-${eventId}`;
}

/**
 * Push an event to GA4 using the Tag Manager
 *
 * @param event
 * @param data
 */
function pushGAEvent(event, data = {}) {
    const dataTotal = {
        event,
        unique_event_id: getUniqueEventId(),
    };

    Object.assign(dataTotal, data);

    window.dataLayer = window.dataLayer || [];

    // Clear all possible options to make sure no tag information is left behind from the previous event
    window.dataLayer.push({
        engagement: null,
        ecommerce: null,
        items: null,
        method: null,
        content_type: null,
        item_id: null,
        unique_event_id: null,
    });
    window.dataLayer.push(dataTotal);
}

/**
 * Attaches and start tracking video events on a given HTMLVideoElement
 *
 * @param {HTMLVideoElement} element
 */
function trackVideoEvents(element) {
    element.addEventListener("play", (event) => {
        pushGAEvent("video-play", {
            video_source: event.target.querySelectorAll("source")[0]?.src,
            duration: event.target.duration,
        });
    });

    element.addEventListener("pause", (event) => {
        const { duration, currentTime } = event.target;
        const progressPercentage = (currentTime / duration) * 100;

        pushGAEvent("video-pause", {
            video_source: event.target.querySelectorAll("source")[0]?.src,
            duration,
            current_time: currentTime,
            progress_percentage: progressPercentage,
        });
    });

    element.addEventListener(
        "timeupdate",
        throttle((event) => {
            const { duration, currentTime } = event.target;
            const progressPercentage = (currentTime / duration) * 100;

            pushGAEvent("video-progress", {
                video_source: event.target.querySelectorAll("source")[0]?.src,
                duration,
                current_time: currentTime,
                progress_percentage: progressPercentage,
            });
        }, 2500)
    );
}

/**
 * Attaches and start tracking Swiper events on a given SwiperJS class
 *
 * @param {import("swiper").Swiper} swiper
 */
function trackSwiperEvents(swiper) {
    swiper.on("slideChange", (event) => {
        pushGAEvent("swiper-slide-change", {
            slide_name: event.el.dataset.slideName,
            slide_index: event.activeIndex,
            slide_direction: event.activeIndex - event.previousIndex,
            slide_count: event.slides.length,
        });
    });
}

/**
 * Attaches click event trackers to all the elements that match the query selector
 *
 * @param {string} selector The elements that need to be tracked, using a query selector
 * @param {string} eventName Push the click event to GTM using this event name
 */
function trackButtonClickEventsBySelector(selector, eventName) {
    document.querySelectorAll(selector).forEach((el) => {
        if (el.dataset.gtmTrackingAdded) {
            // We are already listening to this event

            return;
        }

        el.dataset.gtmTrackingAdded = "true";

        el.addEventListener("click", () => {
            pushGAEvent(eventName, {
                button_selector: selector,
                button_text: el.innerText,
                button_target: el.target,
            });
        });
    });
}

module.exports = {
    pushGAEvent,
    getUniqueEventId,
    trackVideoEvents,
    trackSwiperEvents,
    trackButtonClickEventsBySelector,
};
